import { useEffect, useState } from 'react';
import axios from 'axios';
import { ChevronDown, Loader2 } from 'lucide-react';
import { mlDecimal } from './CfbTicketBuilder';
import { useBetMode } from '../lib/betMode';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sports-betting/board/open-games`;
const pct = (p) => `${Math.min(99, Math.round(p * 100))}%`;
const money = (n) => `$${Math.round(n).toLocaleString()}`;
const timePT = (d, today) => new Date(d).toLocaleString('en-US', { ...(today ? {} : { weekday: 'short', month: 'short', day: 'numeric' }), hour: 'numeric', minute: '2-digit', timeZone: 'America/Los_Angeles' });
const factLine = (f) => f && [f.home && `Home ${f.home}`, f.road && `Road ${f.road}`, f.ppg != null && `${f.ppg} pts/g, allows ${f.papg}`,
  f.last3?.length && `Last 3: ${f.last3.join(', ')}`].filter(Boolean).join(' · ');

const PickRow = ({ p, n }) => {
  const [open, setOpen] = useState(false);
  const mode = useBetMode();
  return (
    <li className="border-t border-zinc-800/70 pt-2 first:border-0 first:pt-0" data-testid={`open-pick-${p.event_id}`}>
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 text-left" data-testid={`open-pick-toggle-${p.event_id}`}>
        <span className="w-5 text-[10px] font-mono text-zinc-500">{n}.</span>
        {p.logo && <img src={p.logo} alt="" className="w-6 h-6 object-contain" />}
        <span className="flex-1 min-w-0 leading-tight">
          <span className="text-[13px] font-semibold text-white">{p.rank ? `#${p.rank} ` : ''}{mode === 'pro' ? `${p.team} ML` : `The ${p.team} win`}</span>
          <span className="block text-[10px] text-zinc-500 truncate">
            <span className="uppercase text-amber-300/80">{p.league}</span> · {p.home_away === 'home' ? 'vs' : '@'} {p.opponent} · {timePT(p.date, p.today)} PT{mode === 'pro' ? ` · ML ${p.ml && p.ml !== 'OFF' ? p.ml : 'not offered'}` : ''}
          </span>
        </span>
        <span className="font-mono text-xs font-bold text-emerald-300">{pct(p.prob)}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-zinc-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <ul className="mt-1.5 ml-7 space-y-0.5 text-[11px] text-zinc-400" data-testid={`open-pick-why-${p.event_id}`}>
          {p.source === 'fpi' && <li className="text-sky-300">No sportsbook line yet — win chance from ESPN's prediction</li>}
          {(p.facts || []).filter((f) => f && factLine(f)).map((f) => <li key={f.abbr} className="text-zinc-300"><b>{f.abbr}</b> · {factLine(f)}</li>)}
          {(p.why || []).filter(Boolean).map((w, i) => <li key={i}>{w}</li>)}
        </ul>
      )}
    </li>
  );
};

const BuiltTicket = ({ legs, size, stake }) => {
  const chance = legs.reduce((a, p) => a * p.prob, 1);
  const dec = legs.reduce((a, p) => a * (mlDecimal(p.ml) || 1), 1);
  const noLine = legs.filter((p) => !mlDecimal(p.ml)).length;
  return (
    <div className="min-w-0 rounded-lg border border-cyan-500/30 bg-zinc-950/60 p-3" data-testid={`open-ticket-${size}`}>
      <div className="flex items-baseline gap-2">
        <p className="flex-1 text-sm font-bold text-white">{size}-leg ticket <span className="font-normal text-zinc-500">{legs.length < size ? `· only ${legs.length} open games` : '· safest open games'}</span></p>
        <span className="font-mono text-sm font-bold text-cyan-300" data-testid={`open-ticket-${size}-chance`}>{Math.round(chance * 100)}% all win</span>
      </div>
      <p className="text-[11px] text-zinc-300 mt-1 leading-snug break-words">{legs.map((p) => p.abbr || p.team).join(' · ')}</p>
      <p className="text-[11px] text-zinc-500 mt-1" data-testid={`open-ticket-${size}-payout`}>{money(stake)} returns about <span className="text-emerald-300 font-semibold">{money(stake * dec)}</span>{noLine ? ` · ${noLine} leg${noLine > 1 ? 's' : ''} with no line not counted` : ''}</p>
    </div>
  );
};

// Team winners for games with no team on an active ticket (shown inside PicksNotOnTickets).
export function OpenGamesBody({ league = 'nfl' }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [stake, setStake] = useState(10);
  useEffect(() => {
    axios.get(API, { params: { league } }).then((r) => setData(r.data)).catch((e) => setErr(e.response?.data?.detail || e.message));
  }, [league]);
  const all = data?.picks || [];
  const picks = all.filter((p) => p.today);
  const later = all.filter((p) => !p.today);
  return (
    <div className="space-y-3 min-w-0" data-testid="open-games-body">
      <p className="text-[11px] text-zinc-500">Win picks for games where <b>no team</b> is on any of your active tickets.</p>
          {!data && !err && <p className="flex items-center gap-2 text-sm text-zinc-400 py-6" data-testid="open-games-loading"><Loader2 className="w-4 h-4 animate-spin" /> Reading your tickets…</p>}
          {err && <p className="text-sm text-red-400" data-testid="open-games-error">{err}</p>}
          {data && (
            <div className="space-y-3 min-w-0">
              <p className="text-[11px] text-zinc-400" data-testid="open-games-summary">
                Checked <b className="text-white">{data.tickets_checked}</b> ticket{data.tickets_checked === 1 ? '' : 's'} · skipped <b className="text-white">{data.skipped.length}</b> game{data.skipped.length === 1 ? '' : 's'} you already have · <b className="text-white">{picks.length}</b> open · <b className="text-white">{later.length}</b> coming up
              </p>
              {picks.length === 0 ? (
                <p className="text-sm text-zinc-400 py-2" data-testid="open-games-empty">No games left that aren't already on your tickets.</p>
              ) : (
                <>
                  <label className="flex items-center gap-1 text-xs text-zinc-400">Stake $
                    <input type="number" min="1" value={stake} onChange={(e) => setStake(Math.max(1, Number(e.target.value) || 1))}
                      className="w-16 rounded bg-zinc-900 border border-zinc-700 px-1.5 py-0.5 text-white" data-testid="open-games-stake" />
                  </label>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <BuiltTicket legs={picks.slice(0, 6)} size={6} stake={stake} />
                    <BuiltTicket legs={picks.slice(0, 8)} size={8} stake={stake} />
                  </div>
                  <p className="text-xs font-semibold text-zinc-300">Open games, safest first</p>
                  <ol className="space-y-2" data-testid="open-games-list">{picks.map((p, i) => <PickRow key={p.event_id} p={p} n={i + 1} />)}</ol>
                </>
              )}
              {later.length > 0 && (
                <>
                  <p className="text-xs font-semibold text-zinc-300">Coming up (not on your tickets)</p>
                  <ol className="space-y-2" data-testid="open-games-later">{later.map((p, i) => <PickRow key={p.event_id} p={p} n={i + 1} />)}</ol>
                </>
              )}
              {data.skipped.length > 0 && (
                <details className="text-[11px] text-zinc-500" data-testid="open-games-skipped">
                  <summary className="cursor-pointer">Skipped (already on a ticket)</summary>
                  <ul className="mt-1 space-y-0.5">{data.skipped.map((s) => <li key={s.event_id}>{s.league.toUpperCase()} {s.game} — {s.on_ticket.join(', ')}</li>)}</ul>
                </details>
              )}
            </div>
          )}
    </div>
  );
}
